import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { s3PublicUrl } from "../../../utils/s3PublicUrl";
import { generateLayout } from "../../../pages/main/crosswordlayout";

const DAY_COUNT = 7;

// Per-day config for question types, character limits, and max questions
const DAY_CONFIG = {
  1: { type: "wordle",  maxQ: 1,  questionLabel: "Word",    questionMax: 5,   answerMax: null, answerLabel: null,  hasOptions: false },
  2: { type: "qa",      maxQ: 5,  questionLabel: "Question",questionMax: 30,  answerMax: 10,   answerLabel: "Answer", hasOptions: false },
  3: { type: "qa",      maxQ: 5,  questionLabel: "Question",questionMax: 100, answerMax: 10,   answerLabel: "Answer", hasOptions: false },
  4: { type: "mcq",     maxQ: 5,  questionLabel: "Question",questionMax: 150, answerMax: 50,   answerLabel: "Answer", hasOptions: true,  optionMax: 50 },
  5: { type: "qa",      maxQ: 5,  questionLabel: "Question",questionMax: 30,  answerMax: 10,   answerLabel: "Answer", hasOptions: false },
  6: { type: "qa",      maxQ: 5,  questionLabel: "Question",questionMax: 100, answerMax: 50,   answerLabel: "Answer", hasOptions: false },
  7: { type: "qa",      maxQ: 10, questionLabel: "Question",questionMax: 150, answerMax: 50,   answerLabel: "Answer", hasOptions: false },
};

const DID_YOU_KNOW_MAX = 250;
const STREAK_MESSAGE_MAX = 150;
const TITLE_MAX = 80;

function buildCrosswordLayoutWords(questions) {
  const list = Array.isArray(questions) ? questions : [];
  return [...list]
    .filter((q) => String(q?.answer ?? '').trim().length >= 2)
    .sort(
      (a, b) =>
        String(b.answer ?? '').replace(/\s/g, '').length -
        String(a.answer ?? '').replace(/\s/g, '').length
    )
    .map((q) => ({
      answer: String(q.answer).replace(/\s/g, '').toUpperCase(),
      question: String(q.question ?? ''),
      did_you_know: String(q.did_you_know ?? ''),
    }));
}

function validateCrosswordCanBuild(questions) {
  try {
    const words = buildCrosswordLayoutWords(questions);
    if (!words.length) return { ok: false, message: 'Add at least one valid answer (2+ characters).' };
    const layout = generateLayout(words);
    const placed = (layout?.result || []).filter(
      (w) => w && w.orientation && w.orientation !== 'none' && w.startx != null && w.starty != null
    );
    if (!placed.length) {
      return { ok: false, message: 'Could not place words in the grid. Try different answers.' };
    }
    if (placed.length < words.length) {
      return {
        ok: false,
        message:
          'Some words could not be placed in the grid. Try different answers (more shared letters / fewer short words).',
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: 'Could not build crossword layout. Try different answers.' };
  }
}

function normalizeStagesData(raw) {
  const base = {};
  for (let i = 1; i <= DAY_COUNT; i++) {
    base[`stage_${i}`] = {
      type: DAY_CONFIG[i].type,
      max_questions: 0,
      rules: [""],
      questions: [],
      badge: "",
      streak: "",
      streak_message: "",
      title: "",
      question_image: "",
      did_you_know_image: "",
      bonus_streak_badge: "",
      bonus_streak_heading: "",
    };
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base;
  const out = { ...base };
  for (let i = 1; i <= DAY_COUNT; i++) {
    const k = `stage_${i}`;
    const s = raw[k];
    if (!s || typeof s !== "object" || Array.isArray(s)) continue;
    out[k] = {
      ...out[k],
      ...s,
      type: DAY_CONFIG[i].type,
      max_questions:
        s.max_questions != null && String(s.max_questions).trim() !== ""
          ? Number(s.max_questions)
          : out[k].max_questions,
      rules: Array.isArray(s.rules) && s.rules.length ? s.rules.map((x) => String(x ?? "")) : [""],
      questions: Array.isArray(s.questions) ? s.questions : [],
      badge: s.badge != null ? String(s.badge) : out[k].badge,
      streak:
        s.streak != null
          ? String(s.streak)
          : s.streakBadge != null
            ? String(s.streakBadge)
            : out[k].streak,
      streak_message: s.streak_message != null ? String(s.streak_message) : out[k].streak_message,
      title: s.title != null ? String(s.title) : out[k].title,
      question_image: s.question_image != null ? String(s.question_image) : out[k].question_image,
      did_you_know_image: s.did_you_know_image != null ? String(s.did_you_know_image) : out[k].did_you_know_image,
      bonus_streak_badge: s.bonus_streak_badge != null ? String(s.bonus_streak_badge) : out[k].bonus_streak_badge,
      bonus_streak_heading: s.bonus_streak_heading != null ? String(s.bonus_streak_heading) : out[k].bonus_streak_heading,
    };
  }
  return out;
}

function clampWithWarn(value, max, fieldName) {
  if (String(value ?? "").length > max) {
    Swal.fire({
      title: "Character Limit Exceeded",
      text: `"${fieldName}" cannot exceed ${max} characters.`,
      icon: "warning",
      confirmButtonText: "OK",
    });
    return String(value).slice(0, max);
  }
  return value;
}

function validateImageFile(file, { width, height, label } = {}) {
  if (!file) return null;
  const isPng = file.type === "image/png" || file.name.toLowerCase().endsWith(".png");
  if (!isPng) {
    Swal.fire({
      title: "Invalid File Type",
      text: `Only PNG images${width && height ? ` (${width}×${height} px)` : ""} are accepted.`,
      icon: "error",
    });
    return null;
  }
  return file;
}

// Character count badge shown below inputs
function CharCount({ value, max }) {
  const len = String(value ?? "").length;
  const near = len >= max * 0.85;
  const over = len > max;
  return (
    <div
      style={{
        textAlign: "right",
        fontSize: 12,
        marginTop: 2,
        color: over ? "#c62828" : near ? "#e65100" : "#6c757d",
        fontWeight: over ? 700 : 400,
      }}
    >
      {len}/{max}
    </div>
  );
}

export default function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [activeTab, setActiveTab] = useState("day"); // 'day' | 'others'
  const [activeDay, setActiveDay] = useState(1);
  const [savingDay, setSavingDay] = useState(false);
  const [savingBadges, setSavingBadges] = useState(false);
  const [savingRulesAndQuestions, setSavingRulesAndQuestions] = useState(false);
  const [savingOthers, setSavingOthers] = useState(false);
  const [stagesData, setStagesData] = useState(() => normalizeStagesData(null));
  const [questionsPairs, setQuestionsPairs] = useState([]);
  const [badgeFile, setBadgeFile] = useState(null);
  const [streakBadgeFile, setStreakBadgeFile] = useState(null);
  const [bonusStreakBadgeFile, setBonusStreakBadgeFile] = useState(null);
  const [questionImageFile, setQuestionImageFile] = useState(null);
  const [didYouKnowImageFile, setDidYouKnowImageFile] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);
  const [streakBadgePreview, setStreakBadgePreview] = useState(null);
  const [bonusStreakBadgePreview, setBonusStreakBadgePreview] = useState(null);
  const [questionImagePreview, setQuestionImagePreview] = useState(null);
  const [didYouKnowImagePreview, setDidYouKnowImagePreview] = useState(null);
  const [lockImageFile, setLockImageFile] = useState(null);
  const [unlockImageFile, setUnlockImageFile] = useState(null);
  const [lockImagePreview, setLockImagePreview] = useState(null);
  const [unlockImagePreview, setUnlockImagePreview] = useState(null);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setStagesData(normalizeStagesData(data.stages_data));
  }, [data]);

  const dayTabs = useMemo(() => Array.from({ length: DAY_COUNT }).map((_, i) => i + 1), []);

  const stageKey = `stage_${activeDay}`;
  const stage = stagesData[stageKey] || normalizeStagesData(null)[stageKey];
  const cfg = DAY_CONFIG[activeDay];
  const effectiveMaxQuestions = useMemo(() => {
    const raw = stage?.max_questions;
    if (raw === "") return cfg.maxQ;
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n)) return cfg.maxQ;
    return Math.min(cfg.maxQ, Math.max(1, n));
  }, [stage?.max_questions, cfg.maxQ]);

  const savedBadgeUrl = useMemo(() => s3PublicUrl(stage?.badge), [stage?.badge]);
  const savedStreakUrl = useMemo(() => s3PublicUrl(stage?.streak), [stage?.streak]);
  const savedBonusStreakUrl = useMemo(() => s3PublicUrl(stage?.bonus_streak_badge), [stage?.bonus_streak_badge]);
  const savedQuestionImageUrl = useMemo(() => s3PublicUrl(stage?.question_image), [stage?.question_image]);
  const savedDidYouKnowImageUrl = useMemo(() => s3PublicUrl(stage?.did_you_know_image), [stage?.did_you_know_image]);
  const savedLockImageUrl = useMemo(() => s3PublicUrl(data?.lock_image), [data?.lock_image]);
  const savedUnlockImageUrl = useMemo(() => s3PublicUrl(data?.unlocked_image), [data?.unlocked_image]);

  useEffect(() => {
    const qs = Array.isArray(stage?.questions) ? stage.questions : [];
    setQuestionsPairs(qs);
    // Reset any *selected* (not-yet-saved) image files when switching day so saved data stays intact.
    setBadgeFile(null);
    setStreakBadgeFile(null);
    setBonusStreakBadgeFile(null);
    setQuestionImageFile(null);
    setDidYouKnowImageFile(null);
    setBadgePreview(null);
    setStreakBadgePreview(null);
    setBonusStreakBadgePreview(null);
    setQuestionImagePreview(null);
    setDidYouKnowImagePreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDay, data]);

  const updateStage = (partial) => {
    setStagesData((prev) => ({
      ...prev,
      [stageKey]: { ...prev[stageKey], ...partial },
    }));
  };

  const updatePairCell = (idx, partial) => {
    setQuestionsPairs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const base = next[idx] && typeof next[idx] === "object" ? next[idx] : {};
      next[idx] = { ...base, ...partial };
      return next;
    });
  };

  // MCQ option helpers
  const updateOption = (pairIdx, optionIdx, value) => {
    const clamped = clampWithWarn(value, cfg.optionMax, `Option ${optionIdx + 1}`);
    setQuestionsPairs((prev) => {
      const next = [...prev];
      const options = Array.isArray(next[pairIdx]?.options) ? [...next[pairIdx].options] : [];
      options[optionIdx] = clamped;
      next[pairIdx] = { ...next[pairIdx], options };
      return next;
    });
  };

  const addOption = (pairIdx) => {
    setQuestionsPairs((prev) => {
      const next = [...prev];
      const options = Array.isArray(next[pairIdx]?.options) ? [...next[pairIdx].options] : [];
      if (options.length >= 4) {
        Swal.fire("Limit Reached", "A question can have at most 4 options.", "warning");
        return prev;
      }
      options.push("");
      next[pairIdx] = { ...next[pairIdx], options };
      return next;
    });
  };

  const removeOption = (pairIdx, optionIdx) => {
    setQuestionsPairs((prev) => {
      const next = [...prev];
      const options = Array.isArray(next[pairIdx]?.options) ? [...next[pairIdx].options] : [];
      if (options.length <= 2) {
        Swal.fire("Minimum Options", "A question must have at least 2 options.", "warning");
        return prev;
      }
      options.splice(optionIdx, 1);
      next[pairIdx] = { ...next[pairIdx], options };
      return next;
    });
  };

  const addPair = () => {
    const currentCount = Array.isArray(questionsPairs) ? questionsPairs.length : 0;
    if (currentCount >= effectiveMaxQuestions) {
      Swal.fire({
        title: "Question Limit Reached",
        text: `Day ${activeDay} allows a maximum of ${effectiveMaxQuestions} question${effectiveMaxQuestions > 1 ? "s" : ""}.`,
        icon: "warning",
      });
      return;
    }
    let newRow;
    if (cfg.type === "wordle") {
      newRow = { word: "", did_you_know: "" };
    } else if (cfg.type === "mcq") {
      newRow = { question: "", options: ["", ""], answer: "", did_you_know: "" };
    } else {
      newRow = { question: "", answer: "", did_you_know: "" };
    }
    setQuestionsPairs((prev) => [...(Array.isArray(prev) ? prev : []), newRow]);
  };

  const deletePair = (idx) => {
    setQuestionsPairs((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next.splice(idx, 1);
      return next;
    });
  };

  // Clear All removed by request (keep per-row delete only).

  const updateRuleCell = (idx, val) => {
    if (String(val ?? "").length > 200) {
      Swal.fire("Limit Exceeded", "Rules can be at most 200 characters.", "warning");
      val = String(val ?? "").slice(0, 200);
    }
    const rules = Array.isArray(stage.rules) ? [...stage.rules] : [""];
    rules[idx] = val;
    updateStage({ rules });
  };

  const addRule = () => {
    const rules = Array.isArray(stage.rules) ? [...stage.rules] : [""];
    if (rules.length >= 6) {
      Swal.fire("Limit Reached", "You can only add up to 6 rules.", "warning");
      return;
    }
    rules.push("");
    updateStage({ rules });
  };

  const removeRule = (idx) => {
    const rules = Array.isArray(stage.rules) ? [...stage.rules] : [""];
    if (rules.length <= 1) {
      Swal.fire("At Least One Rule Required", "You must have at least one rule.", "warning");
      return;
    }
    rules.splice(idx, 1);
    updateStage({ rules });
  };

  // Validate all Q&A pairs before saving
  const validatePairs = (pairs) => {
    for (let i = 0; i < pairs.length; i++) {
      const q = pairs[i];
      const num = i + 1;
      if (cfg.type === "wordle") {
        if (String(q?.word ?? "").length > cfg.questionMax) {
          Swal.fire("Validation Error", `Question ${num}: Word exceeds ${cfg.questionMax} characters.`, "error");
          return false;
        }
      } else if (cfg.type === "mcq") {
        if (String(q?.question ?? "").length > cfg.questionMax) {
          Swal.fire("Validation Error", `Question ${num}: Question text exceeds ${cfg.questionMax} characters.`, "error");
          return false;
        }
        if (String(q?.answer ?? "").length > cfg.answerMax) {
          Swal.fire("Validation Error", `Question ${num}: Answer exceeds ${cfg.answerMax} characters.`, "error");
          return false;
        }
        const options = Array.isArray(q?.options) ? q.options : [];
        if (options.length < 2 || options.length > 4) {
          Swal.fire("Validation Error", `Question ${num}: Options must be between 2 and 4.`, "error");
          return false;
        }
        const norm = (s) => String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
        const seen = new Set();
        for (let o = 0; o < options.length; o++) {
          const v = String(options[o] ?? '');
          if (v.trim() === '') {
            Swal.fire("Validation Error", `Question ${num}: Option ${o + 1} cannot be blank.`, "error");
            return false;
          }
          const key = norm(v);
          if (seen.has(key)) {
            Swal.fire("Validation Error", `Question ${num}: Duplicate options are not allowed.`, "error");
            return false;
          }
          seen.add(key);
        }
        for (let o = 0; o < options.length; o++) {
          if (String(options[o] ?? "").length > cfg.optionMax) {
            Swal.fire("Validation Error", `Question ${num}, Option ${o + 1}: exceeds ${cfg.optionMax} characters.`, "error");
            return false;
          }
        }
        const ans = String(q?.answer ?? '');
        if (ans.trim() === '') {
          Swal.fire("Validation Error", `Question ${num}: Answer cannot be blank.`, "error");
          return false;
        }
        const matches = options.filter((opt) => norm(opt) === norm(ans)).length;
        if (matches !== 1) {
          Swal.fire(
            "Validation Error",
            `Question ${num}: Answer must match exactly one option (no duplicates).`,
            "error"
          );
          return false;
        }
      } else {
        if (String(q?.question ?? "").length > cfg.questionMax) {
          Swal.fire("Validation Error", `Question ${num}: Question text exceeds ${cfg.questionMax} characters.`, "error");
          return false;
        }
        if (String(q?.answer ?? "").length > cfg.answerMax) {
          Swal.fire("Validation Error", `Question ${num}: Answer exceeds ${cfg.answerMax} characters.`, "error");
          return false;
        }
      }
      if (String(q?.did_you_know ?? "").length > DID_YOU_KNOW_MAX) {
        Swal.fire("Validation Error", `Question ${num}: "Did You Know" exceeds ${DID_YOU_KNOW_MAX} characters.`, "error");
        return false;
      }
    }
    return true;
  };

  const handleSaveDay = async ({ goNext = false } = {}) => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    try {
      const questions = (Array.isArray(questionsPairs) ? questionsPairs : []).filter((q) => {
        if (cfg.type === "wordle") return String(q?.word ?? "").trim();
        return String(q?.question ?? "").trim() || String(q?.answer ?? "").trim() || String(q?.did_you_know ?? "").trim();
      });

      if (!validatePairs(questions)) return;

      // Day 3 is crossword: validate that the answers can produce a grid layout.
      if (Number(activeDay) === 3) {
        const cw = validateCrosswordCanBuild(questions);
        if (!cw.ok) {
          Swal.fire("Crossword layout not possible", cw.message, "warning");
          return;
        }
      }

      if (questions.length > cfg.maxQ) {
        Swal.fire("Too Many Questions", `Day ${activeDay} allows a maximum of ${cfg.maxQ} question${cfg.maxQ > 1 ? "s" : ""}.`, "error");
        return;
      }

      const rulesClean = (Array.isArray(stage.rules) ? stage.rules : [])
        .map((r) => String(r ?? "").trim())
        .filter(Boolean);
      if (rulesClean.length === 0) {
        Swal.fire({ title: "Validation", text: "Add at least one game rule.", icon: "error" });
        return;
      }

      if (String(stage.streak_message ?? "").length > STREAK_MESSAGE_MAX) {
        Swal.fire("Validation Error", `Streak Message cannot exceed ${STREAK_MESSAGE_MAX} characters.`, "error");
        return;
      }
      if (String(stage.title ?? "").length > TITLE_MAX) {
        Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
        return;
      }

      const stageOut = {
        ...stage,
        type: cfg.type,
        max_questions:
          stage.max_questions != null && String(stage.max_questions).trim() !== ""
            ? Math.min(cfg.maxQ, Math.max(1, Math.floor(Number(stage.max_questions) || 1)))
            : cfg.maxQ,
        rules: rulesClean.slice(0, 6),
        questions,
        badge: String(stage.badge ?? "").trim(),
        streak: String(stage.streak ?? "").trim(),
        streak_message: String(stage.streak_message ?? "").trim(),
        title: String(stage.title ?? "").trim(),
        question_image: String(stage.question_image ?? "").trim(),
        did_you_know_image: String(stage.did_you_know_image ?? "").trim(),
      };

      setSavingDay(true);
      // Send only the edited day. The backend merges this into the existing
      // stages_data for the theme, so other days are untouched. Sending the
      // full local stagesData snapshot here would re-ship stale copies of
      // other days (their local state only syncs from the server via the
      // background refetch below, which can still be in flight if the admin
      // saves another day quickly via "Save and Continue") and silently
      // revert whatever was just saved for them.
      const payload = {
        data: { stages_data: { [stageKey]: stageOut } },
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        currentTheme,
      };
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", `Day ${activeDay} saved successfully.`, "success");
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
      if (goNext) setActiveDay((d) => Math.min(DAY_COUNT, Number(d) + 1));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save day.", "error");
    } finally {
      setSavingDay(false);
    }
  };

  const handleSaveBadges = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    if (!badgeFile && !streakBadgeFile && !bonusStreakBadgeFile && !questionImageFile && !didYouKnowImageFile) {
      Swal.fire("Warning", "Please select at least one image to upload.", "warning");
      return;
    }

    try {
      setSavingBadges(true);
      const formData = new FormData();
      if (badgeFile) formData.append("badge", badgeFile);
      if (streakBadgeFile) formData.append("streak", streakBadgeFile);
      if (bonusStreakBadgeFile) formData.append("bonus_streak", bonusStreakBadgeFile);
      if (questionImageFile) formData.append("question_image", questionImageFile);
      if (didYouKnowImageFile) formData.append("did_you_know_image", didYouKnowImageFile);
      formData.append("day", activeDay);
      formData.append("currentTheme", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadBadges`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });

      if (!response.ok) throw new Error(`Upload failed with status ${response.status}`);

      Swal.fire("Success", "Badges saved successfully.", "success");
      setBadgeFile(null); setStreakBadgeFile(null); setBonusStreakBadgeFile(null);
      setQuestionImageFile(null); setDidYouKnowImageFile(null);
      setBadgePreview(null); setStreakBadgePreview(null); setBonusStreakBadgePreview(null);
      setQuestionImagePreview(null); setDidYouKnowImagePreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save badges.", "error");
    } finally {
      setSavingBadges(false);
    }
  };

  const handleSaveRulesAndQuestions = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    if (String(stage.streak_message ?? "").length > STREAK_MESSAGE_MAX) {
      Swal.fire("Validation Error", `Streak Message cannot exceed ${STREAK_MESSAGE_MAX} characters.`, "error");
      return;
    }
    if (String(stage.title ?? "").length > TITLE_MAX) {
      Swal.fire("Validation Error", `Title cannot exceed ${TITLE_MAX} characters.`, "error");
      return;
    }

    try {
      setSavingRulesAndQuestions(true);
      const rulesClean = (stage.rules || []).map((r) => String(r ?? "").trim()).filter(Boolean).slice(0, 6);
      if (rulesClean.length === 0) {
        Swal.fire("Validation", "Add at least one game rule.", "error");
        setSavingRulesAndQuestions(false);
        return;
      }

      const stageOut = {
        ...stage,
        rules: rulesClean,
        max_questions:
          stage.max_questions != null && String(stage.max_questions).trim() !== ""
            ? Math.min(cfg.maxQ, Math.max(1, Math.floor(Number(stage.max_questions) || 1)))
            : cfg.maxQ,
        streak_message: String(stage.streak_message ?? "").trim(),
        title: String(stage.title ?? "").trim(),
      };

      // See handleSaveDay: only send the edited day, not the full local
      // stagesData snapshot, to avoid reverting other days from stale state.
      const payload = {
        data: { stages_data: { [stageKey]: stageOut } },
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        currentTheme,
      };
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", `Day ${activeDay} rules and questions saved successfully.`, "success");
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save rules and questions.", "error");
    } finally {
      setSavingRulesAndQuestions(false);
    }
  };

  const handleImageChange = (setter, previewSetter, { width, height, label } = {}) => (e) => {
    const inputEl = e.target;
    const raw = inputEl.files?.[0];
    if (!raw) return;
    const file = validateImageFile(raw, { width, height, label });
    if (!file) {
      inputEl.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (!dataUrl) {
        inputEl.value = "";
        return;
      }
      const img = new Image();
      img.onload = () => {
        const wantsSize = Number.isFinite(Number(width)) && Number.isFinite(Number(height));
        const ok = !wantsSize || (img.width === width && img.height === height);
        if (!ok && wantsSize) {
          Swal.fire({
            title: "Invalid Image Size",
            text: `Image must be exactly ${width}×${height} px.`,
            icon: "error",
          });
          inputEl.value = "";
          return;
        }
        setter(file);
        previewSetter(dataUrl);
        // Allow selecting the same file again later (otherwise change event won't fire).
        inputEl.value = "";
      };
      img.onerror = () => {
        Swal.fire({
          title: "Invalid Image",
          text: "Could not read this PNG file.",
          icon: "error",
        });
        inputEl.value = "";
      };
      img.src = String(dataUrl);
    };
    reader.onerror = () => {
      Swal.fire({
        title: "Invalid Image",
        text: "Could not read this PNG file.",
        icon: "error",
      });
      inputEl.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleSaveOthers = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected.", "error");
      return;
    }
    if (!lockImageFile && !unlockImageFile) {
      Swal.fire("Warning", "Please select at least one image to upload.", "warning");
      return;
    }
    try {
      setSavingOthers(true);
      const formData = new FormData();
      if (lockImageFile) formData.append("lock_image", lockImageFile);
      if (unlockImageFile) formData.append("unlocked_image", unlockImageFile);
      formData.append("currentTheme", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadThemeImages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const resp = await response.json().catch(() => ({}));
      if (!response.ok || !resp.success) {
        throw new Error(resp.message || "Failed to upload theme images");
      }
      Swal.fire("Success", "Images saved successfully.", "success");
      setLockImageFile(null);
      setUnlockImageFile(null);
      setLockImagePreview(null);
      setUnlockImagePreview(null);
      dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save images.", "error");
    } finally {
      setSavingOthers(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  const currentPairCount = Array.isArray(questionsPairs) ? questionsPairs.length : 0;
  const atMaxPairs = currentPairCount >= effectiveMaxQuestions;

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <nav className="themeupdate-tabs" aria-label="Tabs">
        {dayTabs.map((d) => (
          <button
            key={d}
            type="button"
            className={`themeupdate-tab ${
              activeTab === "day" && activeDay === d ? "themeupdate-tab--active" : ""
            }`}
            onClick={() => {
              setActiveTab("day");
              setActiveDay(d);
            }}
          >
            Day {d}
          </button>
        ))}
        <button
          type="button"
          className={`themeupdate-tab ${activeTab === "others" ? "themeupdate-tab--active" : ""}`}
          onClick={() => setActiveTab("others")}
        >
          Others
        </button>
      </nav>

      <div className="themeupdate-panel">
        {activeTab === "others" ? (
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Others - Lock Images</div>
            <div className="themeupdate-card-body">
              <div className="themeupdate-hint-code" style={{ marginBottom: 16 }}>
                Upload the images used for locked/unlocked stages. PNG recommended.
              </div>

              <div className="themeupdate-badges-grid">
                <div className="themeupdate-badge-upload-box">
                  <label className="themeupdate-label">Lock On Image (`lock_image`)</label>
                  <input
                    type="file"
                    accept=".png,image/png"
                    onChange={handleImageChange(setLockImageFile, setLockImagePreview, { width: 100, height: 100 })}
                  />
                  <div className="themeupdate-help"><span>* PNG only, 100×100 px</span></div>
                  {(lockImagePreview || savedLockImageUrl) && (
                    <div style={{ marginTop: 10 }}>
                      <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                        <span>{lockImagePreview ? "Selected image" : "Saved image"}</span>
                      </div>
                      <img
                        src={lockImagePreview || savedLockImageUrl}
                        alt="Lock on"
                        className="themeupdate-preview-img"
                      />
                    </div>
                  )}
                </div>

                <div className="themeupdate-badge-upload-box">
                  <label className="themeupdate-label">Lock Off Image (`unlocked_image`)</label>
                  <input
                    type="file"
                    accept=".png,image/png"
                    onChange={handleImageChange(setUnlockImageFile, setUnlockImagePreview, { width: 100, height: 100 })}
                  />
                  <div className="themeupdate-help"><span>* PNG only, 100×100 px</span></div>
                  {(unlockImagePreview || savedUnlockImageUrl) && (
                    <div style={{ marginTop: 10 }}>
                      <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                        <span>{unlockImagePreview ? "Selected image" : "Saved image"}</span>
                      </div>
                      <img
                        src={unlockImagePreview || savedUnlockImageUrl}
                        alt="Lock off"
                        className="themeupdate-preview-img"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 24 }}>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={handleSaveOthers}
                  disabled={savingOthers}
                >
                  {savingOthers ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* RULES & TOTAL QUESTIONS */}
            <div className="themeupdate-card">
          <div className="themeupdate-card-header">Day {activeDay} - Rules & Questions Count</div>
          <div className="themeupdate-card-body">
            <div className="themeupdate-rules-section">
              <label className="themeupdate-label">Rules</label>
              <div className="themeupdate-rules-repeater">
                {(Array.isArray(stage.rules) ? stage.rules : [""]).map((r, idx) => (
                  <div key={idx}>
                    <div className="themeupdate-rule-row">
                      <input
                        type="text"
                        className="themeupdate-input themeupdate-rule-input"
                        value={r ?? ""}
                        onChange={(e) => updateRuleCell(idx, e.target.value)}
                        placeholder={`Rule ${idx + 1}`}
                        maxLength={200}
                      />
                      <button
                        type="button"
                        className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                        onClick={() => removeRule(idx)}
                        disabled={(stage.rules?.length ?? 1) <= 1}
                        aria-label="Remove rule"
                      >
                        −
                      </button>
                    </div>
                    <CharCount value={r} max={200} />
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary themeupdate-add-rule"
                  onClick={addRule}
                  disabled={(stage.rules?.length ?? 1) >= 6}
                >
                  + Add rule
                </button>
                <div className="themeupdate-help" style={{ marginTop: 6 }}>
                  <span>Max 6 rules. At least 1 required. 200 characters per rule.</span>
                </div>
              </div>
            </div>

            <div className="themeupdate-total-questions">
              <label className="themeupdate-label">
                Total Questions
                <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                  (Max for Day {activeDay}: {cfg.maxQ})
                </span>
              </label>
              <input
                type="number"
                className="themeupdate-input"
                value={
                  stage.max_questions === ""
                    ? ""
                    : stage.max_questions != null && String(stage.max_questions).trim() !== ""
                      ? stage.max_questions
                      : currentPairCount
                }
                min={1}
                max={cfg.maxQ}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    updateStage({ max_questions: "" });
                    return;
                  }
                  const n = Math.floor(Number(raw));
                  if (!Number.isFinite(n)) return;
                  updateStage({ max_questions: Math.min(cfg.maxQ, Math.max(1, n)) });
                }}
              />
              <div className="themeupdate-help">
                <span>* Set how many questions should be used in Day {activeDay} (1–{cfg.maxQ})</span>
              </div>
            </div>

            <div className="themeupdate-total-questions">
              <label className="themeupdate-label">
                Streak Message
                <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                  (Max {STREAK_MESSAGE_MAX} chars)
                </span>
              </label>
              <input
                type="text"
                className="themeupdate-input"
                value={stage.streak_message ?? ""}
                maxLength={STREAK_MESSAGE_MAX}
                onChange={(e) => {
                  const val = clampWithWarn(e.target.value, STREAK_MESSAGE_MAX, "Streak Message");
                  updateStage({ streak_message: val });
                }}
                placeholder="Message shown with the streak badge"
              />
              <CharCount value={stage.streak_message} max={STREAK_MESSAGE_MAX} />
            </div>

            <div className="themeupdate-total-questions">
              <label className="themeupdate-label">
                Title
                <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
                  (Max {TITLE_MAX} chars)
                </span>
              </label>
              <input
                type="text"
                className="themeupdate-input"
                value={stage.title ?? ""}
                maxLength={TITLE_MAX}
                onChange={(e) => {
                  const val = clampWithWarn(e.target.value, TITLE_MAX, "Title");
                  updateStage({ title: val });
                }}
                placeholder="Day title or completion message"
              />
              <CharCount value={stage.title} max={TITLE_MAX} />
            </div>

            <div className="themeupdate-actions" style={{ marginTop: 24 }}>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-success"
                onClick={handleSaveRulesAndQuestions}
                disabled={savingRulesAndQuestions}
              >
                {savingRulesAndQuestions ? "Saving…" : "Save Rules & Questions"}
              </button>
            </div>
          </div>
        </div>

        {/* QUESTIONS & ANSWERS SECTION */}
        <div className="themeupdate-card">
          <div className="themeupdate-card-header">
            Day {activeDay} - {cfg.type === "wordle" ? "Wordle Words" : cfg.type === "mcq" ? "MCQ Questions & Answers" : "Questions & Answers"}
            <span style={{ fontWeight: 400, fontSize: 13, color: "#6c757d", marginLeft: 8 }}>
              ({currentPairCount}/{effectiveMaxQuestions})
            </span>
          </div>
          <div className="themeupdate-card-body">

            {/* Per-day character limit info banner */}
            <div className="themeupdate-hint-code" style={{ marginBottom: 16 }}>
              {cfg.type === "wordle" && (
                <>Day 1 (Wordle): Word max <strong>{cfg.questionMax}</strong> chars · Did You Know max <strong>{DID_YOU_KNOW_MAX}</strong> chars · Max <strong>{cfg.maxQ}</strong> question</>
              )}
              {cfg.type === "mcq" && (
                <>Day {activeDay} (MCQ): Question max <strong>{cfg.questionMax}</strong> · Answer max <strong>{cfg.answerMax}</strong> · Each option max <strong>{cfg.optionMax}</strong> chars · Did You Know max <strong>{DID_YOU_KNOW_MAX}</strong> · Max <strong>{cfg.maxQ}</strong> questions</>
              )}
              {cfg.type === "qa" && (
                <>Day {activeDay}: Question max <strong>{cfg.questionMax}</strong> · Answer max <strong>{cfg.answerMax}</strong> chars · Did You Know max <strong>{DID_YOU_KNOW_MAX}</strong> chars · Max <strong>{cfg.maxQ}</strong> questions</>
              )}
            </div>

            <div className="themeupdate-pairs-grid" role="list">
              {(Array.isArray(questionsPairs) ? questionsPairs : []).map((q, idx) => (
                <div className="themeupdate-pair-card" key={idx} role="listitem">
                  <button
                    type="button"
                    className="themeupdate-pair-delete"
                    onClick={() => deletePair(idx)}
                    aria-label="Delete pair"
                    title="Delete"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>

                  {/* WORDLE */}
                  {cfg.type === "wordle" && (
                    <div>
                      <div className="themeupdate-pair-row">
                        <div className="themeupdate-pair-label">Word</div>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            className="themeupdate-input themeupdate-input-tight"
                            value={q?.word ?? ""}
                            maxLength={cfg.questionMax}
                            onChange={(e) => {
                              // Day 1 word: no spaces, only letters, max 5 chars.
                              const cleaned = String(e.target.value ?? '')
                                .replace(/\s+/g, '')
                                .replace(/[^a-zA-Z]/g, '');
                              const val = clampWithWarn(cleaned, cfg.questionMax, "Word");
                              updatePairCell(idx, { word: val });
                            }}
                            placeholder={`Max ${cfg.questionMax} chars`}
                          />
                          <CharCount value={q?.word} max={cfg.questionMax} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MCQ */}
                  {cfg.type === "mcq" && (
                    <>
                      <div className="themeupdate-pair-row">
                        <div className="themeupdate-pair-label">Question</div>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            className="themeupdate-input themeupdate-input-tight"
                            value={q?.question ?? ""}
                            maxLength={cfg.questionMax}
                            onChange={(e) => {
                              const val = clampWithWarn(e.target.value, cfg.questionMax, "Question");
                              updatePairCell(idx, { question: val });
                            }}
                            placeholder={`Max ${cfg.questionMax} chars`}
                          />
                          <CharCount value={q?.question} max={cfg.questionMax} />
                        </div>
                      </div>

                      {/* Options */}
                      <div style={{ marginBottom: 8 }}>
                        <div className="themeupdate-pair-label" style={{ marginBottom: 6 }}>Options</div>
                        {(Array.isArray(q?.options) ? q.options : ["", ""]).map((opt, oi) => (
                          <div key={oi} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                            <span style={{ color: "#fff", fontSize: 12, minWidth: 20 }}>{oi + 1}.</span>
                            <div style={{ flex: 1 }}>
                              <input
                                type="text"
                                className="themeupdate-input themeupdate-input-tight"
                                value={opt ?? ""}
                                maxLength={cfg.optionMax}
                                onChange={(e) => updateOption(idx, oi, e.target.value)}
                                placeholder={`Option ${oi + 1}, max ${cfg.optionMax} chars`}
                              />
                              <CharCount value={opt} max={cfg.optionMax} />
                            </div>
                            <button
                              type="button"
                              className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-danger"
                              style={{ padding: "4px 8px" }}
                              onClick={() => removeOption(idx, oi)}
                            >
                              −
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-sm themeupdate-btn-primary"
                          style={{ marginTop: 4 }}
                          onClick={() => addOption(idx)}
                          disabled={(q?.options?.length ?? 0) >= 4}
                        >
                          + Option
                        </button>
                      </div>

                      <div className="themeupdate-pair-row">
                        <div className="themeupdate-pair-label">Answer</div>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            className="themeupdate-input themeupdate-input-tight"
                            value={q?.answer ?? ""}
                            maxLength={cfg.answerMax}
                            onChange={(e) => {
                              const val = clampWithWarn(e.target.value, cfg.answerMax, "Answer");
                              updatePairCell(idx, { answer: val });
                            }}
                            placeholder={`Correct answer, max ${cfg.answerMax} chars`}
                          />
                          <CharCount value={q?.answer} max={cfg.answerMax} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* STANDARD Q&A */}
                  {cfg.type === "qa" && (
                    <>
                      <div className="themeupdate-pair-row">
                        <div className="themeupdate-pair-label">Question</div>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            className="themeupdate-input themeupdate-input-tight"
                            value={q?.question ?? ""}
                            maxLength={cfg.questionMax}
                            onChange={(e) => {
                              const val = clampWithWarn(e.target.value, cfg.questionMax, "Question");
                              updatePairCell(idx, { question: val });
                            }}
                            placeholder={`Max ${cfg.questionMax} chars`}
                          />
                          <CharCount value={q?.question} max={cfg.questionMax} />
                        </div>
                      </div>
                      <div className="themeupdate-pair-row">
                        <div className="themeupdate-pair-label">Answer</div>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            className="themeupdate-input themeupdate-input-tight"
                            value={q?.answer ?? ""}
                            maxLength={cfg.answerMax}
                            onChange={(e) => {
                              const val = clampWithWarn(e.target.value, cfg.answerMax, "Answer");
                              updatePairCell(idx, { answer: val });
                            }}
                            placeholder={`Max ${cfg.answerMax} chars`}
                          />
                          <CharCount value={q?.answer} max={cfg.answerMax} />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Did You Know — all types */}
                  <div className="themeupdate-pair-row">
                    <div className="themeupdate-pair-label">Did you know</div>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        className="themeupdate-input themeupdate-input-tight"
                        value={q?.did_you_know ?? ""}
                        maxLength={DID_YOU_KNOW_MAX}
                        onChange={(e) => {
                          const val = clampWithWarn(e.target.value, DID_YOU_KNOW_MAX, "Did You Know");
                          updatePairCell(idx, { did_you_know: val });
                        }}
                        placeholder={`Optional fact, max ${DID_YOU_KNOW_MAX} chars`}
                      />
                      <CharCount value={q?.did_you_know} max={DID_YOU_KNOW_MAX} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="themeupdate-actions themeupdate-actions--wrap" style={{ marginTop: 18 }}>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-neutral-outline"
                onClick={addPair}
                disabled={atMaxPairs}
                title={atMaxPairs ? `Maximum ${cfg.maxQ} questions for Day ${activeDay}` : "Add a new question"}
              >
                Add {cfg.type === "wordle" ? "Word" : "Pair"}
                {atMaxPairs && <span style={{ marginLeft: 6, fontSize: 11, color: "#c62828" }}>(Max reached)</span>}
              </button>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-success"
                onClick={() => handleSaveDay({ goNext: true })}
                disabled={savingDay}
              >
                {savingDay ? "Saving…" : "Save and Continue"}
              </button>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-danger"
                style={{ display: "none" }}
                disabled={true}
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>
          </div>
        </div>

        {/* BADGE UPLOADS SECTION */}
            <div className="themeupdate-card">
          <div className="themeupdate-card-header">Day {activeDay} - Badges</div>
          <div className="themeupdate-card-body">
            <div className="themeupdate-hint-code" style={{ marginBottom: 16 }}>
              All images must be <strong>PNG format</strong>, recommended size <strong>534 × 534 px</strong>.
            </div>

            <div className="themeupdate-badges-grid">
              {/* Badge Upload */}
              <div className="themeupdate-badge-upload-box">
                <label className="themeupdate-label">Badge Image</label>
                <input
                  type="file"
                  accept=".png,image/png"
                  onChange={handleImageChange(setBadgeFile, setBadgePreview, { width: 534, height: 534 })}
                />
                <div className="themeupdate-help"><span>* PNG only, 534×534 px</span></div>
                {(badgePreview || savedBadgeUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{badgePreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img src={badgePreview || savedBadgeUrl} alt="Badge" className="themeupdate-preview-img" />
                  </div>
                )}
              </div>

              {/* Streak Badge Upload */}
              <div className="themeupdate-badge-upload-box">
                <label className="themeupdate-label">Streak Badge Image</label>
                <input
                  type="file"
                  accept=".png,image/png"
                  onChange={handleImageChange(setStreakBadgeFile, setStreakBadgePreview, { width: 534, height: 534 })}
                />
                <div className="themeupdate-help"><span>* PNG only, 534×534 px</span></div>
                {(streakBadgePreview || savedStreakUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{streakBadgePreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img src={streakBadgePreview || savedStreakUrl} alt="Streak badge" className="themeupdate-preview-img" />
                  </div>
                )}
              </div>

              {/* Question Image Upload (hide for Day 1 + Day 3) */}
              {![1, 3].includes(activeDay) && (
                <div className="themeupdate-badge-upload-box">
                  <label className="themeupdate-label">Question Image</label>
                  <input
                    type="file"
                    accept=".png,image/png"
                    onChange={handleImageChange(setQuestionImageFile, setQuestionImagePreview, { width: 800, height: 400 })}
                  />
                  <div className="themeupdate-help"><span>* PNG only, 800×400 px</span></div>
                  {(questionImagePreview || savedQuestionImageUrl) && (
                    <div style={{ marginTop: 10 }}>
                      <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                        <span>{questionImagePreview ? "Selected image" : "Saved image"}</span>
                      </div>
                      <img src={questionImagePreview || savedQuestionImageUrl} alt="Question" className="themeupdate-preview-img" />
                    </div>
                  )}
                </div>
              )}

              {/* Did You Know Popup Image Upload */}
              <div className="themeupdate-badge-upload-box">
                <label className="themeupdate-label">Did You Know Popup Image</label>
                <input
                  type="file"
                  accept=".png,image/png"
                  onChange={handleImageChange(setDidYouKnowImageFile, setDidYouKnowImagePreview, { width: 534, height: 534 })}
                />
                <div className="themeupdate-help"><span>* PNG only, 534×534 px</span></div>
                {(didYouKnowImagePreview || savedDidYouKnowImageUrl) && (
                  <div style={{ marginTop: 10 }}>
                    <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                      <span>{didYouKnowImagePreview ? "Selected image" : "Saved image"}</span>
                    </div>
                    <img src={didYouKnowImagePreview || savedDidYouKnowImageUrl} alt="Did you know" className="themeupdate-preview-img" />
                  </div>
                )}
              </div>

              {/* Bonus Streak Badge Upload - Only for days 3, 5, 7 */}
              {[3, 5, 7].includes(activeDay) && (
                <div className="themeupdate-badge-upload-box">
                  <label className="themeupdate-label">Bonus Streak Badge Image</label>
                  <input
                    type="file"
                    accept=".png,image/png"
                    onChange={handleImageChange(setBonusStreakBadgeFile, setBonusStreakBadgePreview, { width: 534, height: 534 })}
                  />
                  <div className="themeupdate-help"><span>PNG only, 534×534 px — Optional</span></div>
                  {stage?.bonus_streak_heading && (
                    <div style={{ marginTop: 10 }}>
                      <div className="themeupdate-help" style={{ color: "#704232", fontWeight: "bold" }}>
                        {stage.bonus_streak_heading}
                      </div>
                    </div>
                  )}
                  {(bonusStreakBadgePreview || savedBonusStreakUrl) && (
                    <div style={{ marginTop: 10 }}>
                      <div className="themeupdate-help" style={{ color: "#6c757d" }}>
                        <span>{bonusStreakBadgePreview ? "Selected image" : "Saved image"}</span>
                      </div>
                      <img src={bonusStreakBadgePreview || savedBonusStreakUrl} alt="Bonus streak badge" className="themeupdate-preview-img" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="themeupdate-actions" style={{ justifyContent: "center", marginTop: 24 }}>
              <button
                type="button"
                className="themeupdate-btn themeupdate-btn-success"
                onClick={handleSaveBadges}
                disabled={savingBadges}
              >
                {savingBadges ? "Saving Badges…" : "Save Badges"}
              </button>
            </div>
          </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
